import { MessageDescriptor } from "react-intl";

import useI18n from "@I18n/hooks/useI18n";

import { MAX_PHOTO_FILE_SIZE_BYTES } from "../config/constants";
import messages from "../messages";
import {
  PHOTO_REJECTION,
  PhotoRejection,
  selectUploadablePhotos,
} from "../utils/photoSelection";
import { usePhotoToast } from "./usePhotoToast";

/**
 * The wording per rule, in its three shapes: the plain one when the selection
 * was dropped whole, and the `.partial` pair when some of it still got through
 * — `one` for a single survivor, `other` for several.
 *
 * One flat string per form rather than an ICU `plural` block: the branch lives
 * in `wordingFor` below, which is how the rest of the app does it too
 * (`week.one` / `week.other`). Translators get one sentence per key.
 */
interface IRejectionCopy {
  whole: MessageDescriptor;
  partialOne: MessageDescriptor;
  partialOther: MessageDescriptor;
}

const REJECTION_MESSAGES: Record<PhotoRejection, IRejectionCopy> = {
  [PHOTO_REJECTION.UNSUPPORTED_FORMAT]: {
    whole: messages["taskCreation.photos.error.unsupportedFormat"],
    partialOne:
      messages["taskCreation.photos.error.unsupportedFormat.partial.one"],
    partialOther:
      messages["taskCreation.photos.error.unsupportedFormat.partial.other"],
  },
  [PHOTO_REJECTION.TOO_LARGE]: {
    whole: messages["taskCreation.photos.error.tooLarge"],
    partialOne: messages["taskCreation.photos.error.tooLarge.partial.one"],
    partialOther: messages["taskCreation.photos.error.tooLarge.partial.other"],
  },
  [PHOTO_REJECTION.LIMIT_REACHED]: {
    whole: messages["taskCreation.photos.error.limitReached"],
    partialOne: messages["taskCreation.photos.error.limitReached.partial.one"],
    partialOther:
      messages["taskCreation.photos.error.limitReached.partial.other"],
  },
};

/**
 * Which of a rule's three wordings a selection earned.
 *
 * `addedCount` is what survived the rule, so 0 means the selection was dropped
 * whole and the copy says nothing about a count.
 */
const wordingFor = (copy: IRejectionCopy, addedCount: number) => {
  if (addedCount === 0) return copy.whole;

  return addedCount === 1 ? copy.partialOne : copy.partialOther;
};

/**
 * The size limit as the copy quotes it, in Mo.
 *
 * The French wording carries the number ("Les photos de plus de {maxSize} Mo
 * n'ont pas été ajoutées") while the English source does not, so it is handed
 * to every message here and ignored by the ones that never name it.
 */
const MAX_PHOTO_FILE_SIZE_MB = MAX_PHOTO_FILE_SIZE_BYTES / (1024 * 1024);

export interface IUsePhotoSelectionParams {
  /** Lowercase, without the dot. Empty means no declared restriction. */
  allowedExtensions: string[];
  /** The whole allowance, only ever quoted back in the limit copy. */
  maxFiles: number;
  /** `maxFiles` minus what is already added or still uploading. */
  availableSlots: number;
}

/**
 * The gate every picked file passes through, on both screens that have one.
 *
 * The rules live in `utils/photoSelection`, which is pure; this hook is the
 * half that talks — it turns each broken rule into one toast, worded by
 * whether anything survived the selection.
 *
 * The screens differ in where their bounds come from (the photos screen reads
 * the question's `constraints`, the description screen has no config yet and
 * uses `DESCRIPTION_PHOTO_LIMITS`), so they are parameters rather than
 * something this hook looks up.
 */
export const usePhotoSelection = ({
  allowedExtensions,
  maxFiles,
  availableSlots,
}: IUsePhotoSelectionParams) => {
  const { formatMessage } = useI18n();
  const showPhotoToast = usePhotoToast();

  const announce = (rejected: PhotoRejection[], addedCount: number) =>
    rejected.forEach((reason) =>
      showPhotoToast(
        formatMessage(wordingFor(REJECTION_MESSAGES[reason], addedCount), {
          count: addedCount,
          max: maxFiles,
          maxSize: MAX_PHOTO_FILE_SIZE_MB,
        })
      )
    );

  return {
    /**
     * What of a picker selection may be uploaded. Everything it drops has been
     * reported by the time this returns.
     */
    selectFiles: (files: FileList | File[] | null): File[] => {
      if (!files) return [];

      const { accepted, rejected } = selectUploadablePhotos(Array.from(files), {
        allowedExtensions,
        maxFileSizeBytes: MAX_PHOTO_FILE_SIZE_BYTES,
        availableSlots,
      });

      announce(rejected, accepted.length);

      return accepted;
    },

    /**
     * The picker was never opened, because nothing could have fitted. Says so
     * in the same words a selection that overflowed would have used.
     */
    notifyLimitReached: () =>
      showPhotoToast(
        formatMessage(messages["taskCreation.photos.error.limitReached"], {
          max: maxFiles,
        })
      ),
  };
};
